import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import ImgFallback from "@/components/ImgFallback";
import {
  MapPin, Clock, LockKey, LockKeyOpen, Sparkle, ChatCircle, Phone,
  ShieldCheck, Timer, Warning, Calendar,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

function timeUntil(iso) {
  const now = new Date();
  const t = new Date(iso);
  const mins = Math.round((t - now) / 60000);
  if (mins < 0) return "Now";
  if (mins < 60) return `in ${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h < 24) return `in ${h}h ${m}m`;
  const d = Math.floor(h / 24);
  return `in ${d}d`;
}

export default function Upcoming() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [demoUnlock, setDemoUnlock] = useState(false);

  const load = () => {
    if (!user) { setLoading(false); return; }
    api.get("/bookings/mine").then((r) => {
      setBookings(r.data || []);
    }).finally(() => setLoading(false));
  };
  useEffect(load, [user]);

  if (!user) {
    return (
      <section className="max-w-md mx-auto px-6 py-24 text-center" data-testid="upcoming-signin">
        <p className="overline mb-3">Sign in required</p>
        <h1 className="font-display text-3xl">Sign in to see your upcoming bookings.</h1>
        <Link to="/auth" className="btn-gold inline-block mt-8 px-6 py-3 rounded-full text-xs uppercase tracking-[0.25em] font-semibold">Sign in</Link>
      </section>
    );
  }

  if (loading) return <div className="max-w-3xl mx-auto px-5 py-10 text-cream/50" data-testid="upcoming-loading">Loading…</div>;

  if (!bookings.length) {
    return (
      <section className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="upcoming-empty">
        <p className="overline mb-3">Nothing booked yet</p>
        <h1 className="font-display text-3xl">Your next appointment lives here.</h1>
        <p className="text-cream/70 mt-4">When you book a Studio, KUTZ Pass and address-unlock live here.</p>
        <Link to="/discover" className="btn-gold inline-block mt-8 px-6 py-3 rounded-full text-xs uppercase tracking-[0.25em] font-semibold">Discover Studios</Link>
      </section>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-5 pt-8 pb-24" data-testid="upcoming-page">
      <p className="overline mb-2">Upcoming</p>
      <h1 className="font-display text-4xl md:text-5xl tracking-tighter text-cream">Your bookings</h1>

      <div className="mt-4 mb-8 flex items-center gap-3" data-testid="upcoming-demo-toggle">
        <p className="text-[10px] uppercase tracking-[0.25em] text-cream/40">Demo state</p>
        <div className="flex gap-1 border border-cream/15 p-1 rounded-full">
          {[["locked", false], ["released", true]].map(([label, val]) => (
            <button
              key={label}
              data-testid={`demo-address-${label}`}
              onClick={() => setDemoUnlock(val)}
              className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] ${demoUnlock === val ? "bg-gold text-ink font-semibold" : "text-cream/70"}`}
            >
              Address {label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-5">
        {bookings.map((b) => (
          <UpcomingCard key={b.id} b={b} demoUnlock={demoUnlock} />
        ))}
      </div>
    </div>
  );
}

function UpcomingCard({ b, demoUnlock }) {
  const [studio, setStudio] = useState(null);
  const [detail, setDetail] = useState(null);
  const [runningLate, setRunningLate] = useState(null);
  useEffect(() => {
    api.get(`/studios/${b.studio_id}`).then((r) => setStudio(r.data)).catch(() => setStudio({ area: "—", city: "—", professional_name: "—", name: b.studio_name }));
    api.get(`/bookings/${b.id}`).then((r) => setDetail(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b.studio_id, b.id]);

  // Real reveal: 90 min before start. Demo toggle overrides.
  const startMs = new Date(b.start_time).getTime();
  const naturalReveal = (startMs - Date.now()) <= 90 * 60 * 1000;
  const revealed = demoUnlock || naturalReveal;
  // Booking detail exposes exact_address when revealed by backend; fall back to demo stub.
  const exactAddress =
    detail?.exact_address ||
    (revealed ? `Studio 4, ${studio?.area || "Darwin City"}, ${studio?.city || "Darwin"} NT` : null);

  const runLate = (m) => {
    setRunningLate(m);
    toast.success(`Studio notified — ${m}+ minutes late.`);
  };

  return (
    <div className="border border-cream/10 overflow-hidden" data-testid={`upcoming-${b.id}`}>
      {studio && (
        <div className="relative">
          <ImgFallback src={studio.hero_look || studio.cover_image} alt={studio.name} className="w-full h-48 object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/95 via-ink/40 to-transparent" />
          <div className="absolute bottom-4 left-4 right-4">
            <p className="text-xs text-gold uppercase tracking-[0.25em]">{timeUntil(b.start_time)}</p>
            <p className="font-display text-2xl text-cream tracking-tight">{b.studio_name}</p>
            <p className="text-xs text-cream/70">{b.service_name} · with {studio?.professional_name}</p>
          </div>
        </div>
      )}
      <div className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm text-cream flex items-center gap-2">
              <Clock size={14} color="#C5A059" weight="fill" />
              {new Date(b.start_time).toLocaleString(undefined, { weekday: "long", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}
            </p>
            <p className="text-xs text-cream/60 mt-1">${b.price} · {b.duration_minutes} min</p>
          </div>
          <span className="chip text-[10px]">{b.status.replace("_", " ")}</span>
        </div>

        {/* Address state */}
        {revealed ? (
          <div className="border border-gold/40 bg-gold/5 p-4 flex items-start gap-3" data-testid={`address-released-${b.id}`}>
            <LockKeyOpen size={20} color="#C5A059" weight="fill" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-gold">Address released</p>
              <p className="text-sm text-cream mt-1">{exactAddress}</p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-cream/50 mt-1">Please arrive on time.</p>
            </div>
          </div>
        ) : (
          <div className="border border-cream/10 p-4 flex items-start gap-3" data-testid={`address-locked-${b.id}`}>
            <LockKey size={20} color="#C5A059" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-cream/60">Address locked</p>
              <p className="text-sm text-cream mt-1">Approximately {studio?.area}, {studio?.city} · {studio?.distance_km} km</p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-cream/50 mt-1">Unlocks 90 minutes before your appointment.</p>
            </div>
          </div>
        )}

        {/* KUTZ Pass */}
        <div className="border border-cream/10 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldCheck size={20} color="#C5A059" weight="fill" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-gold">KUTZ Pass</p>
              <p className="text-sm text-cream">
                {b.status === "completed" ? "Appointment complete" : "Show this at arrival"}
              </p>
            </div>
          </div>
          {b.status !== "completed" && (
            <Link to={`/kutz-pass/${b.id}`} data-testid={`view-pass-${b.id}`} className="btn-ghost px-4 py-2 rounded-full text-[10px] uppercase tracking-[0.2em]">View Pass</Link>
          )}
        </div>

        {b.status === "completed" && (
          <div className="border border-gold/40 bg-gold/5 p-4" data-testid={`completed-${b.id}`}>
            <p className="text-[10px] uppercase tracking-[0.25em] text-gold">Completed</p>
            <p className="text-sm text-cream mt-1">Six-hour service-dispute window {b.dispute_window_end ? `open until ${new Date(b.dispute_window_end).toLocaleTimeString()}` : "opens at checkout"}.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link to={`/review/new/${b.id}`} data-testid={`review-${b.id}`} className="btn-gold px-4 py-2 rounded-full text-[10px] uppercase tracking-[0.2em] font-semibold">Leave verified review</Link>
              <Link to={`/booking/new/${b.studio_id}`} data-testid={`rebook-${b.id}`} className="btn-ghost px-4 py-2 rounded-full text-[10px] uppercase tracking-[0.2em]">Book {b.studio_name} again</Link>
            </div>
            <div className="mt-3 border-t border-cream/10 pt-3">
              <p className="text-[10px] uppercase tracking-[0.25em] text-cream/60">Reward</p>
              <p className="text-xs text-cream/80 mt-1">Your next booking with {b.studio_name} gives {b.studio_name} a KUTZ fee-free booking.</p>
            </div>
          </div>
        )}

        {b.is_repeat_discount && b.status !== "completed" && (
          <div className="border border-gold/40 bg-gold/5 p-4" data-testid={`fee-waiver-${b.id}`}>
            <p className="text-[10px] uppercase tracking-[0.25em] text-gold">KUTZ fee waived on this booking</p>
            <p className="text-xs text-cream/80 mt-1">You're paying the normal service price. The Professional pays 0% KUTZ fee on this repeat.</p>
          </div>
        )}

        {/* Running Late */}
        <div>
          <p className="overline mb-2 flex items-center gap-2"><Timer size={12} weight="fill" color="#C5A059" /> Running late?</p>
          <div className="flex gap-2 flex-wrap">
            {[5, 10, 15].map((m) => (
              <button
                key={m}
                onClick={() => runLate(m)}
                data-testid={`late-${m}`}
                className={`chip ${runningLate === m ? "border-gold bg-gold/10 text-gold" : "hover:border-cream/40"}`}
              >
                {m === 15 ? "15+ min" : `${m} min`}
              </button>
            ))}
          </div>
        </div>

        {/* Contact + Safety */}
        <div className="border-t border-cream/10 pt-4 grid grid-cols-3 gap-2">
          <Link to={`/messages/${b.id}`} data-testid={`action-message-${b.id}`} className="btn-ghost py-3 rounded-full text-[10px] uppercase tracking-[0.2em] inline-flex items-center justify-center gap-1">
            <ChatCircle size={14} /> Message
          </Link>
          <button data-testid={`action-call-${b.id}`} onClick={() => toast.info("Masked call — placeholder in v1 demo.")} className="btn-ghost py-3 rounded-full text-[10px] uppercase tracking-[0.2em] inline-flex items-center justify-center gap-1">
            <Phone size={14} /> Call
          </button>
          <Link to={`/safety/${b.id}`} data-testid={`action-safety-${b.id}`} className="btn-ghost py-3 rounded-full text-[10px] uppercase tracking-[0.2em] inline-flex items-center justify-center gap-1 text-red-300 border-red-300/30 hover:border-red-300">
            <Warning size={14} /> Safety
          </Link>
        </div>
      </div>
    </div>
  );
}
